/**
 * Tago Tours pricing solver — copia de la app.
 *
 * Mismas constantes y mismas fórmulas que `supabase/functions/_shared/tours-pricing.ts`.
 * `scripts/validate-tours-pricing-parity.mjs` compara las dos copias contra el
 * source de cada archivo: si esta copia o la del Edge Function cambia sola, el
 * `pnpm lint` falla — la app mostraría un precio y tours-quote autorizaría otro.
 *
 * Modelo del doc (monetización §6): el guest paga el precio FINAL ("precio simple
 * afuera"), que incluye el servicio de Tago, el pago protegido y el soporte; el
 * host guarda `host_net_cents` (el neto que espera POR VEHÍCULO — el grupo
 * charteriza el vehículo completo y reparte el costo entre sus cabezas); el
 * solver deriva el gross del vehículo:
 *
 *   Neto   = Gross − adquirencia − impuestos
 *   Margen = 15% × Neto
 *   Payout = Neto − margen
 *
 * Todo dinero en centavos (USD). Los costos de adquirencia e impuestos son
 * variables de backend: acá viven los valores por defecto que el contrato fija
 * hasta que el producto los module.
 */
export const TOURS_PRICING_DEFAULTS = {
  serviceMarginRate: 0.15,
  acquirerRate: 0.035,
  taxRate: 0.0,
} as const

export type ToursPricingParams = {
  serviceMarginRate: number
  acquirerRate: number
  taxRate: number
}

export const toursParamsAreValid = (params: ToursPricingParams): boolean =>
  Number.isFinite(params.serviceMarginRate) &&
  params.serviceMarginRate > 0 &&
  params.serviceMarginRate < 1 &&
  Number.isFinite(params.acquirerRate) &&
  params.acquirerRate >= 0 &&
  params.acquirerRate < 1 &&
  Number.isFinite(params.taxRate) &&
  params.taxRate >= 0 &&
  params.taxRate < 1 &&
  params.acquirerRate + params.taxRate < 1

/**
 * Gross (precio final al guest por el vehículo completo) que deja al host
 * exactamente el `hostNetCents` esperado después de adquirencia, impuestos y
 * margen de servicio.
 */
export function hostNetToGross(
  hostNetCents: number,
  params: ToursPricingParams = TOURS_PRICING_DEFAULTS,
): number {
  if (!Number.isSafeInteger(hostNetCents) || hostNetCents < 1) {
    throw new Error('hostNetCents must be a positive integer')
  }
  if (!toursParamsAreValid(params)) {
    throw new Error('invalid tours pricing params')
  }
  const payoutShare = (1 - params.serviceMarginRate) * (1 - params.acquirerRate - params.taxRate)
  // El epsilon corrige el ruido de punto flotante en la frontera: 16405 / 0.82025
  // da 20000.000000000004 en IEEE754, y sin el epsilon el ceil subía un centavo
  // un precio que matemáticamente es exacto. No cambia casos no-frontera.
  return Math.ceil(hostNetCents / payoutShare - 1e-9)
}

/**
 * Convierte un precio final ya calibrado en el menor neto que puede representar
 * sin publicar jamás un total menor al modelo. El resultado se vuelve a pasar
 * por `hostNetToGross` antes de almacenarlo, por lo que la experiencia, la
 * cotización, la reserva y el pago muestran exactamente la misma economía.
 */
export function grossToHostNetAtOrAbove(
  grossCents: number,
  params: ToursPricingParams = TOURS_PRICING_DEFAULTS,
): number {
  if (!Number.isSafeInteger(grossCents) || grossCents < 1) {
    throw new Error('grossCents must be a positive integer')
  }
  if (!toursParamsAreValid(params)) {
    throw new Error('invalid tours pricing params')
  }
  const payoutShare = (1 - params.serviceMarginRate) * (1 - params.acquirerRate - params.taxRate)
  let low = 1
  let high = Math.max(1, Math.ceil(grossCents * payoutShare))
  while (hostNetToGross(high, params) < grossCents) {
    high *= 2
  }
  while (low < high) {
    const mid = Math.floor((low + high) / 2)
    if (hostNetToGross(mid, params) >= grossCents) high = mid
    else low = mid + 1
  }
  return low
}

/**
 * Desglose de un gross: adquirencia, impuestos, neto, margen de servicio y
 * payout al host. Espejo de los ledger kinds `acquiring_fee`, `tax`,
 * `service_fee`, `payout` del contrato.
 */
export function toursGrossBreakdown(
  grossCents: number,
  params: ToursPricingParams = TOURS_PRICING_DEFAULTS,
): {
  grossCents: number
  acquiringCents: number
  taxCents: number
  netCents: number
  marginCents: number
  payoutCents: number
} {
  if (!Number.isSafeInteger(grossCents) || grossCents < 1) {
    throw new Error('grossCents must be a positive integer')
  }
  if (!toursParamsAreValid(params)) {
    throw new Error('invalid tours pricing params')
  }
  const acquiringCents = Math.round(grossCents * params.acquirerRate)
  const taxCents = Math.round(grossCents * params.taxRate)
  const netCents = grossCents - acquiringCents - taxCents
  const marginCents = Math.round(netCents * params.serviceMarginRate)
  const payoutCents = netCents - marginCents
  return { grossCents, acquiringCents, taxCents, netCents, marginCents, payoutCents }
}

/**
 * La tarifa de Tours que ve el viajero nunca lleva centavos: si hay fracción,
 * se aproxima hacia arriba al siguiente dólar entero. Los centavos se conservan
 * solamente en los cálculos internos de comisiones y payout.
 */
export function toursRoundUpToNextDollarCents(cents: number): number {
  if (!Number.isSafeInteger(cents) || cents < 0) {
    throw new Error('cents must be a non-negative integer')
  }
  return Math.ceil(cents / 100) * 100
}

/**
 * Precio POR PERSONA dinámico: el costo del vehículo repartido entre las cabezas
 * del grupo, redondeado SIEMPRE al siguiente dólar entero.
 *
 * El tour se cobra por vehículo (total fijo = `vehicleCents`); este número es lo
 * que cada cabeza paga (o su "desde" si el grupo aún no llena el vehículo). Se
 * redondea hacia ARRIBA para nunca prometer una cuota por debajo del real.
 */
export function toursPerPersonFromVehicleCents(vehicleCents: number, passengers: number): number {
  if (!Number.isSafeInteger(vehicleCents) || vehicleCents < 0) {
    throw new Error('vehicleCents must be a non-negative integer')
  }
  if (!Number.isSafeInteger(passengers) || passengers < 1) {
    throw new Error('passengers must be a positive integer')
  }
  return toursRoundUpToNextDollarCents(Math.ceil(vehicleCents / passengers))
}
